import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { BoxPrimitive } from '#pplx/frontend/aether/primitives/BoxPrimitive/index.js';
import { TextPrimitive } from '#pplx/frontend/aether/primitives/TextPrimitive/index.js';
import { sortTickerSuggestions } from '#pplx/frontend/apps/ask/suggest-dropdown/primitives/components/SuggestCategoriesShared.js';
import { SuggestionCard } from '#pplx/frontend/apps/ask/suggest-dropdown/primitives/components/SuggestionCard.js';
import { usePromoShimmerEligibility } from '#pplx/frontend/apps/ask/suggest-dropdown/primitives/hooks/usePromoSuggestionShimmer.js';
import classNames from 'classnames';
import React, { useCallback, useMemo } from 'react';
const GroupItemSeparator = React.memo(function GroupItemSeparator(props) {
    const { text, show, className } = props;
    if (!show) {
        return null;
    }
    return (_jsxs(_Fragment, { children: [_jsx(BoxPrimitive, { className: classNames('my-2 sm:mx-2 border-b first:hidden', className) }), text && (_jsx(BoxPrimitive, { className: "sm:mx-2 mt-1 mb-2 pr-2", children: _jsx(TextPrimitive
                // Prevent wrapping for non-english languages
                , { 
                    // Prevent wrapping for non-english languages
                    className: "w-max", variant: "tinyMono", color: "light", children: text }) }))] }));
});
export const SuggestionGroup = React.memo(function SuggestionGroup({ category, suggestions, categoryConfig, value, focusedIndex, setFocusedIndex, handleSubmit, indexOffset, LeftAccessoryComponent, RightAccessoryComponent, renderQueryContent, renderSuggestionItem, onFillButtonClick, titleClassName, }) {
    // Sort ticker suggestions to place those with invalid metadata at the bottom
    const sortedSuggestions = useMemo(() => (category === 'ticker' ? sortTickerSuggestions(suggestions) : suggestions), [category, suggestions]);
    const { eligible: shimmerEligible, domain: shimmerDomain, markSeen: onShimmerComplete, } = usePromoShimmerEligibility();
    if (!suggestions.length)
        return null;
    return (_jsxs(_Fragment, { children: [_jsx(GroupItemSeparator, { show: categoryConfig.showHeader, text: categoryConfig.displayName, className: titleClassName }), sortedSuggestions.map((suggestion, index) => (_jsx(SuggestionCardFactory, { suggestion: suggestion, index: index, category: category, categoryConfig: categoryConfig, value: value, focusedIndex: focusedIndex, setFocusedIndex: setFocusedIndex, handleSubmit: handleSubmit, indexOffset: indexOffset, LeftAccessoryComponent: LeftAccessoryComponent, RightAccessoryComponent: RightAccessoryComponent, onFillButtonClick: onFillButtonClick, renderQueryContent: renderQueryContent, renderSuggestionItem: renderSuggestionItem, highlightPrefix: categoryConfig.highlightPrefix ?? true, shimmerEligible: shimmerEligible, shimmerDomain: shimmerDomain, onShimmerComplete: onShimmerComplete }, `${category}-${suggestion.identifier || suggestion.query_uuid || suggestion.input + suggestion.completion}`)))] }));
});
const SuggestionCardFactory = React.memo(function SuggestionCardFactory({ suggestion: { input, completion, query_uuid, image, imageDark, url = '', description, identifier = '', category, title = '', subscribed = false, metadata, submission_query, inputs_and_completions, icon_name, shouldShowTitle, actionHandler, isPersonalized, isPromo, isLearnAboutMe, }, index, LeftAccessoryComponent, RightAccessoryComponent, renderQueryContent, renderSuggestionItem, highlightPrefix, focusedIndex, handleSubmit, indexOffset, onFillButtonClick, setFocusedIndex, value, shimmerEligible, shimmerDomain, onShimmerComplete, }) {
    const submit = useCallback((query) => handleSubmit({
        query,
        query_uuid,
        url,
        identifier,
        category,
        description,
        image,
        imageDark,
        title,
        subscribed,
        submission_query,
        isPersonalized,
        isPromo,
        isLearnAboutMe,
    }, index + indexOffset), [
        category,
        description,
        handleSubmit,
        identifier,
        image,
        imageDark,
        index,
        indexOffset,
        query_uuid,
        submission_query,
        subscribed,
        title,
        url,
        isPersonalized,
        isPromo,
        isLearnAboutMe,
    ]);
    const fill = useCallback(() => {
        onFillButtonClick(submission_query ?? input + completion, url);
    }, [completion, input, onFillButtonClick, submission_query, url]);
    const leftIcon = useMemo(() => (_jsx(LeftAccessoryComponent, { image: image, imageDark: imageDark, alt: input + value, url: url, category: category, iconName: icon_name, value: value })), [LeftAccessoryComponent, category, icon_name, image, imageDark, input, url, value]);
    const rightIcon = useMemo(() => (_jsx(RightAccessoryComponent, { onClick: fill, url: url, isSelected: subscribed, metadata: metadata, category: category })), [RightAccessoryComponent, fill, url, subscribed, metadata, category]);
    const suggestion = useMemo(() => ({
        input,
        completion,
        inputs_and_completions,
        query_uuid,
        title,
        image,
        imageDark,
        url,
        icon_name,
        description,
        identifier,
        category,
        subscribed,
        metadata,
        submission_query,
        shouldShowTitle,
        actionHandler,
        isPersonalized,
        isPromo,
        isLearnAboutMe,
    }), [
        actionHandler,
        category,
        completion,
        description,
        icon_name,
        identifier,
        image,
        imageDark,
        input,
        inputs_and_completions,
        isLearnAboutMe,
        isPersonalized,
        isPromo,
        metadata,
        query_uuid,
        shouldShowTitle,
        submission_query,
        subscribed,
        title,
        url,
    ]);
    return (_jsx(SuggestionCard, { onClick: submit, query: input, suggestion: suggestion, completion: completion, inputsAndCompletions: inputs_and_completions, url: url, suggestionIndex: index + indexOffset, keyboardFocusedIndex: focusedIndex, setKeyboardFocusedIndex: setFocusedIndex, description: description, title: title, shouldShowTitle: shouldShowTitle, highlightPrefix: highlightPrefix, className: classNames(
        // highlight if hover with mouse
        'hover:bg-subtle', 
        // highlight if focus with keyboard
        'data-[focused=self]:bg-subtle', 'dark:data-[focused=self]:bg-subtle', 
        // unhighlight if hover with mouse and other suggestion is focused
        'hover:data-[focused=other]:bg-raised', 'dark:hover:data-[focused=other]:bg-subtle'), category: category, renderQueryContent: renderQueryContent, renderSuggestionItem: renderSuggestionItem, metadata: metadata, leftIcon: leftIcon, rightIcon: rightIcon, actionHandler: actionHandler, shimmer: isPromo && shimmerEligible, shimmerKey: shimmerDomain, onShimmerComplete: onShimmerComplete }));
});
//# sourceMappingURL=SuggestCategories.js.map